import { useState } from 'react';
import { fmt, pct, formatDateTime, timeAgo } from '../lib/formatters';

function PositionRow({ pos, leverage, balance, moneyDigits, onClose, onChart }) {
  const dec = pos.entryPrice > 100 ? 2 : pos.entryPrice > 1 ? 4 : 6;
  const sideClass = pos.side === 'buy' ? 'text-success' : 'text-error';
  const sidePrefix = pos.side === 'buy' ? 'L' : 'S';
  const pnlClass = pos.unrealizedPnl >= 0 ? 'text-success' : 'text-error';
  const leverageStr = leverage ? `${leverage}x` : '-';
  const opened = new Date(pos.openTime);

  // Margin = actual money locked for this position
  const md = Math.pow(10, moneyDigits || 2);
  const marginUsd = (pos.usedMargin || 0) / md;
  const marginPct = balance > 0 ? (marginUsd / balance) * 100 : 0;

  const volumeUnits = (pos.volume || 0) / 100;
  const slPct = pos.stopLoss
    ? ((pos.stopLoss - pos.entryPrice) / pos.entryPrice * 100 * (pos.side === 'buy' ? 1 : -1))
    : null;
  const tpPct = pos.takeProfit
    ? ((pos.takeProfit - pos.entryPrice) / pos.entryPrice * 100 * (pos.side === 'buy' ? 1 : -1))
    : null;
  // Convert potential P&L to USD for non-USD quoted pairs
  const sym = pos.symbol || '';
  const quoteToUsd = (val) => {
    if (sym.includes('JPY') && !sym.startsWith('USD')) return val / 159;
    if (sym.startsWith('USD') && !sym.endsWith('USD')) return val / pos.entryPrice;
    return val;
  };
  const potentialLoss = pos.stopLoss
    ? quoteToUsd(Math.abs(pos.entryPrice - pos.stopLoss) * volumeUnits)
    : null;
  const potentialProfit = pos.takeProfit
    ? quoteToUsd(Math.abs(pos.takeProfit - pos.entryPrice) * volumeUnits)
    : null;

  const [confirming, setConfirming] = useState(false);

  const handleClose = () => {
    if (!confirming) {
      setConfirming(true);
      setTimeout(() => setConfirming(false), 3000);
      return;
    }
    onClose?.(pos.id, pos.volume);
    setConfirming(false);
  };

  return (
    <tr>
      <td className="font-mono font-bold whitespace-nowrap">
        <span className={sideClass}>{sidePrefix}</span>{' '}
        <button className="link link-hover" onClick={() => onChart?.(pos.symbol, pos.side)}>
          {pos.symbol.replace('USD', '')}
        </button>
      </td>
      <td className="font-mono">{fmt(pos.entryPrice, dec)}</td>
      <td className="font-mono">{pos.currentPrice ? fmt(pos.currentPrice, dec) : '-'}</td>
      <td className={`font-mono font-bold ${pnlClass}`}>
        {fmt(pos.unrealizedPnl)}
        <div className="text-xs opacity-80">{pct(pos.pnlPercent)}</div>
      </td>
      <td className="font-mono text-xs">
        {fmt(marginUsd, 0)}
        <div className="opacity-60">{pct(marginPct)} bal</div>
      </td>
      <td className="font-mono text-center">{leverageStr}</td>
      <td className="font-mono text-xs">
        <div className="text-success">
          <span className="font-bold">Profit: {potentialProfit != null ? fmt(potentialProfit) : '-'}</span>
          <div className="opacity-40">{pos.takeProfit ? fmt(pos.takeProfit, dec) : '-'}{tpPct != null && ` (${pct(tpPct)})`}</div>
        </div>
        <div className="text-error">
          <span className="font-bold">Loss: {potentialLoss != null ? fmt(potentialLoss) : '-'}</span>
          <div className="opacity-40">{pos.stopLoss ? fmt(pos.stopLoss, dec) : '-'}{slPct != null && ` (${pct(slPct)})`}</div>
        </div>
      </td>
      <td className="text-xs hidden lg:table-cell">
        <span className="opacity-70">{formatDateTime(opened)}</span>
        <div className="opacity-50">{timeAgo(opened)}</div>
      </td>
      <td>
        <button
          className={`btn btn-xs ${confirming ? 'btn-error' : 'btn-ghost text-error'}`}
          onClick={handleClose}
        >
          {confirming ? 'Confirm?' : 'Close'}
        </button>
      </td>
    </tr>
  );
}

export default function PositionsTable({ positions, leverage, totalPnl, balance, moneyDigits, onClose, onChart }) {
  const totalPnlClass = totalPnl >= 0 ? 'text-success' : 'text-error';

  return (
    <div className="card bg-base-200 shadow-xl">
      <div className="card-body p-4 lg:p-6">
        <h2 className="card-title text-base lg:text-lg">
          Open Positions
          <span className="badge badge-primary badge-sm">{positions.length}</span>
        </h2>
        <div className="overflow-x-auto -mx-2">
          <table className="table table-zebra table-sm lg:table-md">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Entry</th>
                <th>Current</th>
                <th>P&L</th>
                <th>Margin</th>
                <th>Lev</th>
                <th>SL / TP</th>
                <th className="hidden lg:table-cell">Opened</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 ? (
                <tr><td colSpan={9} className="text-center opacity-60 py-8">No open positions</td></tr>
              ) : (
                <>
                  {positions.map(pos => (
                    <PositionRow key={pos.id} pos={pos} leverage={leverage} balance={balance} moneyDigits={moneyDigits} onClose={onClose} onChart={onChart} />
                  ))}
                  <tr className="border-t-2 border-base-300 font-bold">
                    <td colSpan={3} className="text-right opacity-70">Total P&L</td>
                    <td className={`font-mono ${totalPnlClass}`}>{fmt(totalPnl)}</td>
                    <td colSpan={5}></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
